import { useEffect, useState } from "react";
import { WelcomeVideoBanner } from "@/components/WelcomeVideoBanner";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useNavigate } from "react-router-dom";
import { fmtDate } from "@/components/DatePicker";
import api, { usd } from "@/lib/api";
import { StatCard } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Wallet, TrendingUp, Clock, CalendarCheck, FileText, ArrowRight } from "lucide-react";
import { MyTeamCard } from "@/components/MyTeamCard";
import { MyTrainingCard } from "@/components/MyTrainingCard";

export default function EmployeeDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/employee").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-muted-foreground">Loading dashboard…</div>;
  const e = data.employee || {};

  return (
    <div className="space-y-6 animate-fade-up" data-testid="employee-dashboard">
      <InstallPrompt />
      <WelcomeVideoBanner />
      <Card className="p-6 bg-gradient-to-br from-primary/8 to-blue-500/5 flex items-center gap-4 flex-wrap">
        <Avatar className="w-16 h-16"><AvatarImage src={e.avatar} /><AvatarFallback>{e.name?.[0]}</AvatarFallback></Avatar>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight">Welcome back, {e.name?.split(" ")[0]}</h1>
          <p className="text-sm text-muted-foreground">{e.job_title} · {e.department}</p>
        </div>
        <Button data-testid="quick-timesheet" variant="outline" className="w-full sm:w-auto" onClick={() => navigate("/timesheet")}>
          <Clock className="w-4 h-4 mr-1.5" /> Clock In / Out
        </Button>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <StatCard testid="emp-ytd-gross" label="YTD Gross" value={usd(data.ytd_gross)} icon={Wallet} />
        <StatCard testid="emp-ytd-net" label="YTD Net Pay" value={usd(data.ytd_net)} icon={TrendingUp} accent="text-emerald-600" />
        <StatCard testid="emp-week-hours" label="Hours This Week" value={data.week_hours?.toFixed(1)} icon={Clock} accent="text-blue-600" />
        <StatCard testid="emp-pto" label="PTO Balance" value={`${data.pto_balance} days`} icon={CalendarCheck} accent="text-amber-600" sub={data.pto_balances?.filter((b) => b.key !== "pto").map((b) => `${b.name} ${b.balance}`).join(" · ") || undefined} />
      </div>

      <MyTrainingCard />
      <MyTeamCard />

      {data.last_slip && (
        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold font-display flex items-center gap-2"><FileText className="w-4 h-4" /> Latest Paystub</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/payslips")}>View all <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 font-mono-num">
            <div><div className="text-xs text-muted-foreground">Pay Date</div><div className="font-semibold">{fmtDate(data.last_slip.pay_date)}</div></div>
            <div><div className="text-xs text-muted-foreground">Gross</div><div className="font-semibold">{usd(data.last_slip.gross_pay)}</div></div>
            <div><div className="text-xs text-muted-foreground">Deductions</div><div className="font-semibold text-amber-600">{usd(data.last_slip.total_deductions)}</div></div>
            <div><div className="text-xs text-muted-foreground">Net Pay</div><div className="font-semibold text-emerald-600">{usd(data.last_slip.net_pay)}</div></div>
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="p-5 border-b border-border"><h3 className="font-semibold font-display">Recent Shifts</h3></div>
        <div className="divide-y divide-border">
          {data.recent_timesheets?.map((t) => (
            <div key={t.id} className="flex items-center justify-between p-4">
              <div className="font-mono-num text-sm">{fmtDate(t.date)}</div>
              <div className="text-sm text-muted-foreground">
                {t.clock_in ? new Date(t.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                {" – "}
                {t.clock_out ? new Date(t.clock_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
              </div>
              <div className="font-mono-num font-semibold">{t.hours?.toFixed(2)} h</div>
            </div>
          ))}
          {(!data.recent_timesheets || data.recent_timesheets.length === 0) && (
            <div className="p-6 text-center text-muted-foreground text-sm">No shifts recorded yet. Clock in to get started.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
